import { useMutation, useQuery } from "@apollo/client";
import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Modal,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";

import { ConfirmDialog } from "@/components/molecules/ConfirmDialog";
import {
  CallPlanDetailFragment,
  DeactivateCallPlanMutation,
  DeactivateCallPlanMutationVariables,
  DeleteCallPlanMutation,
  DeleteCallPlanMutationVariables,
  GetCallPlanDetailQuery,
  GetCallPlanDetailQueryVariables,
  ReactivateCallPlanMutation,
  ReactivateCallPlanMutationVariables,
  RemoveInteractionsMutation,
  RemoveInteractionsMutationVariables,
} from "@/graphql/graphql";
import { StatusChip } from "@/pages/UserDetails/components/Playlists/components/StatusChip";
import { formatAccountCalendarDate, secondsToHMS } from "@/utils/date-utils";
import { formatGqlExecutionErrors, graphqlClientErrorMessage } from "@/utils/format";

import {
  deactivateCallPlanConfirmDialogContent,
  reactivateCallPlanConfirmDialogContent,
} from "../CallPlans.columns";
import { CALL_PLAN_SNACKBAR_AUTO_HIDE_MS, callPlanSnackbarOnClose } from "../CallPlans.snackbar";
import { DELETE_CALL_PLAN_MUTATION } from "../CreateCallPlanModal/CreateCallPlanModal.mutations";
import {
  modalCloseGlyphSx,
  modalCloseIconButtonSx,
  modalContainerSx,
  modalHeaderSx,
  modalTitleSx,
} from "../CreateCallPlanModal/CreateCallPlanModal.styles";
import { canReactivateCallPlan } from "../CreateCallPlanModal/CreateCallPlanModal.utils";
import { PatientTimezoneDateBlurb } from "../PatientTimezoneDateBlurb";
import {
  type ActivityInteractionDetail,
  type CallPlanDetailInteraction,
  InteractionSection,
} from "./InteractionSection";
import {
  DEACTIVATE_CALL_PLAN_MUTATION,
  REACTIVATE_CALL_PLAN_MUTATION,
  REMOVE_INTERACTIONS_MUTATION,
} from "./ViewCallPlanModal.mutations";
import { GET_CALL_PLAN_DETAIL_QUERY } from "./ViewCallPlanModal.queries";
import * as sx from "./ViewCallPlanModal.styles";
import type {
  ViewCallPlanBodyProps,
  ViewCallPlanDetail,
  ViewCallPlanMetaSectionProps,
  ViewCallPlanModalHeaderProps,
  ViewCallPlanModalProps,
} from "./ViewCallPlanModal.types";

const estimateActivityInteractionSeconds = (interaction: ActivityInteractionDetail): number => {
  let sec = 0;

  const audioSeconds = interaction.voiceMessage?.audioFile?.durationInSeconds;
  if (interaction.estimatedDuration && interaction.estimatedDuration > 0) {
    sec += interaction.estimatedDuration;
  } else if (audioSeconds) {
    sec += audioSeconds;
  }

  sec += interaction.contents.reduce((sum, c) => {
    if (c.__typename === "QuestionActivityContent") {
      return sum + (c.silenceBeforeResponseSec ?? 0) + (c.responseTimeLimitSec ?? 0);
    }

    return sum;
  }, 0);

  return Math.round(sec);
};

const estimateCallPlanDetailSeconds = (
  interactions?: CallPlanDetailInteraction[] | null
): number => {
  if (!interactions?.length) {
    return 0;
  }

  return interactions.reduce((total, interaction) => {
    if (interaction.__typename === "ActivityInteraction") {
      return total + estimateActivityInteractionSeconds(interaction);
    }

    return total;
  }, 0);
};

/** Blocks staging deletes only when plan dates show it ended. */
const interactionRemoveBlockedReasonForEndedPlanOnly = (
  callPlan?: ViewCallPlanDetail | null
): string | null => {
  if (!callPlan?.endDate) {
    return null;
  }
  if (new Date() > new Date(callPlan.endDate)) {
    return "Call plan has ended.";
  }

  return null;
};

const ViewCallPlanModalHeader: React.FC<ViewCallPlanModalHeaderProps> = ({
  callPlanToView,
  onRequestCloseModal,
  editState,
  deleteState,
  deleteInProgress,
  deactivateInProgress,
  reactivateInProgress,
  showReactivate,
  showSaveChanges,
  saveChangesInProgress,
  onRequestSaveChanges,
  onRequestDelete,
  onRequestDeactivate,
  onRequestReactivate,
  onRequestEdit,
}) => {
  const handleEditClick = useCallback(() => {
    if (callPlanToView && !editState?.disabled) {
      onRequestEdit(callPlanToView);
    }
  }, [callPlanToView, editState?.disabled, onRequestEdit]);

  const handleDeleteClick = useCallback(() => {
    if (callPlanToView && !deleteState?.disabled && !deleteInProgress) {
      onRequestDelete();
    }
  }, [callPlanToView, deleteInProgress, deleteState?.disabled, onRequestDelete]);

  const handleDeactivateClick = useCallback(() => {
    if (callPlanToView?.isActive && !deactivateInProgress) {
      onRequestDeactivate();
    }
  }, [callPlanToView?.isActive, deactivateInProgress, onRequestDeactivate]);

  const handleReactivateClick = useCallback(() => {
    if (showReactivate && !reactivateInProgress) {
      onRequestReactivate();
    }
  }, [onRequestReactivate, reactivateInProgress, showReactivate]);

  return (
    <Box sx={modalHeaderSx}>
      <Typography variant="h4" sx={modalTitleSx}>
        Call Plan
      </Typography>
      <Box sx={sx.viewCallPlanHeaderActionsSx}>
        {showSaveChanges && (
          <Button
            disableElevation
            disabled={saveChangesInProgress}
            aria-busy={saveChangesInProgress}
            sx={sx.viewCallPlanHeaderEditButtonSx(true)}
            onClick={onRequestSaveChanges}
          >
            {saveChangesInProgress ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              "Save changes"
            )}
          </Button>
        )}
        <Tooltip title={editState?.disabledTooltip}>
          <span>
            <Button
              disableElevation
              disabled={editState?.disabled}
              sx={sx.viewCallPlanHeaderEditButtonSx(!editState?.disabled)}
              onClick={handleEditClick}
            >
              Edit call plan
            </Button>
          </span>
        </Tooltip>
        {callPlanToView?.isActive && (
          <Button
            disableElevation
            disabled={deactivateInProgress}
            aria-busy={deactivateInProgress}
            sx={sx.viewCallPlanHeaderEditButtonSx(true)}
            onClick={handleDeactivateClick}
          >
            {deactivateInProgress ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              "Deactivate call plan"
            )}
          </Button>
        )}
        {showReactivate && (
          <Button
            disableElevation
            disabled={reactivateInProgress}
            aria-busy={reactivateInProgress}
            sx={sx.viewCallPlanHeaderEditButtonSx(true)}
            onClick={handleReactivateClick}
          >
            {reactivateInProgress ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              "Reactivate call plan"
            )}
          </Button>
        )}
        <Tooltip title={deleteState?.disabledTooltip}>
          <span>
            <Button
              disableElevation
              disabled={deleteState?.disabled || deleteInProgress}
              sx={sx.viewCallPlanHeaderDeleteButtonSx(!deleteState?.disabled)}
              onClick={handleDeleteClick}
            >
              {deleteInProgress ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                "Delete call plan"
              )}
            </Button>
          </span>
        </Tooltip>
        <IconButton
          onClick={onRequestCloseModal}
          aria-label="Close"
          size="small"
          sx={modalCloseIconButtonSx}
        >
          <CloseIcon sx={modalCloseGlyphSx} />
        </IconButton>
      </Box>
    </Box>
  );
};

const ViewCallPlanMetaSection: React.FC<ViewCallPlanMetaSectionProps> = ({
  accountTimeZone,
  callPlan,
  estimatedLengthSeconds,
}) => (
  <Box sx={sx.viewCallPlanMetaRowSx}>
    <Stack spacing={0} sx={sx.viewCallPlanMetaStackRoundSx}>
      <Typography sx={sx.viewCallPlanMetaCaptionSx} component="span">
        Round
      </Typography>
      <Box sx={sx.viewCallPlanMetaValueCellSx}>
        <Typography variant="body1">{callPlan.program.round}</Typography>
      </Box>
    </Stack>
    <Stack spacing={0} sx={sx.viewCallPlanMetaStackWeekSx}>
      <Typography sx={sx.viewCallPlanMetaCaptionSx} component="span">
        Week
      </Typography>
      <Box sx={sx.viewCallPlanMetaValueCellSx}>
        <Typography variant="body1">{callPlan.programWeek}</Typography>
      </Box>
    </Stack>
    <Stack spacing={0} sx={sx.viewCallPlanMetaStackFieldSx}>
      <Typography sx={sx.viewCallPlanMetaCaptionSx} component="span">
        Start date
      </Typography>
      <Box sx={sx.viewCallPlanMetaValueCellSx}>
        <Typography variant="body1">
          {formatAccountCalendarDate(callPlan.startDate, accountTimeZone)}
        </Typography>
      </Box>
    </Stack>
    <Stack spacing={0} sx={sx.viewCallPlanMetaStackFieldSx}>
      <Typography sx={sx.viewCallPlanMetaCaptionSx} component="span">
        End date
      </Typography>
      <Box sx={sx.viewCallPlanMetaValueCellSx}>
        <Typography variant="body1">
          {callPlan.endDate
            ? formatAccountCalendarDate(callPlan.endDate.toString(), accountTimeZone)
            : "Active"}
        </Typography>
      </Box>
    </Stack>
    <Stack spacing={0} sx={sx.viewCallPlanMetaStackFieldSx}>
      <Typography sx={sx.viewCallPlanMetaCaptionSx} component="span">
        Estimated length
      </Typography>
      <Box sx={sx.viewCallPlanMetaValueCellSx}>
        <Typography variant="body1">
          {estimatedLengthSeconds > 0 ? secondsToHMS(estimatedLengthSeconds) : "-"}
        </Typography>
      </Box>
    </Stack>
    <Stack spacing={0} sx={sx.viewCallPlanMetaStackFieldSx}>
      <Typography sx={sx.viewCallPlanMetaCaptionSx} component="span">
        Status
      </Typography>
      <Box sx={sx.viewCallPlanMetaValueCellSx}>
        <StatusChip isActive={callPlan.isActive} />
      </Box>
    </Stack>
  </Box>
);

const ViewCallPlanBody: React.FC<ViewCallPlanBodyProps> = ({
  loading,
  error,
  sortedInteractions,
  interactionRemoveBlockedReason,
  onStageRemoveInteraction,
  interactionStagingFrozen,
}) => {
  if (loading) {
    return (
      <Box sx={sx.viewCallPlanScrollSpinnerWrapSx}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" variant="body2">
        Could not load this call plan.
      </Typography>
    );
  }

  if (!sortedInteractions.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        This call plan has no interactions yet.
      </Typography>
    );
  }

  return (
    <>
      {sortedInteractions.map((interaction) => (
        <InteractionSection
          key={interaction.id}
          interaction={interaction}
          interactionRemoveBlockedReason={interactionRemoveBlockedReason}
          onStageRemoveInteraction={onStageRemoveInteraction}
          blockInteractionStaging={interactionStagingFrozen}
        />
      ))}
    </>
  );
};

const ViewCallPlanModal: React.FC<ViewCallPlanModalProps> = ({
  open,
  accountTimeZone,
  callPlans = [],
  callPlanToView,
  editState,
  deleteState,
  onClose,
  onRequestEdit,
}) => {
  const [pendingRemovalInteractionIds, setPendingRemovalInteractionIds] = useState<number[]>([]);
  const [unsavedStagingClosePromptOpen, setUnsavedStagingClosePromptOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deactivateConfirmOpen, setDeactivateConfirmOpen] = useState(false);
  const [deleteErrorOpen, setDeleteErrorOpen] = useState(false);
  const [deleteErrorMessage, setDeleteErrorMessage] = useState<string | null>(null);
  const [deactivateErrorOpen, setDeactivateErrorOpen] = useState(false);
  const [deactivateErrorMessage, setDeactivateErrorMessage] = useState<string | null>(null);
  const [reactivateConfirmOpen, setReactivateConfirmOpen] = useState(false);
  const [reactivateErrorOpen, setReactivateErrorOpen] = useState(false);
  const [reactivateErrorMessage, setReactivateErrorMessage] = useState<string | null>(null);
  const [removeInteractionsErrorOpen, setRemoveInteractionsErrorOpen] = useState(false);
  const [removeInteractionsErrorMessage, setRemoveInteractionsErrorMessage] = useState<
    string | null
  >(null);
  const [removeInteractionsSuccessOpen, setRemoveInteractionsSuccessOpen] = useState(false);
  const [detailedCallPlan, setDetailedCallPlan] = useState<CallPlanDetailFragment | null>(null);

  const { data, loading, error } = useQuery<
    GetCallPlanDetailQuery,
    GetCallPlanDetailQueryVariables
  >(GET_CALL_PLAN_DETAIL_QUERY, {
    skip: !open || !callPlanToView,
    variables: { callPlanId: callPlanToView.id },
    fetchPolicy: "cache-and-network",
  });

  const [deleteCallPlan, { loading: deleteInProgress }] = useMutation<
    DeleteCallPlanMutation,
    DeleteCallPlanMutationVariables
  >(DELETE_CALL_PLAN_MUTATION, { refetchQueries: ["GetSingleUser"] });

  const [deactivateCallPlan, { loading: deactivateInProgress }] = useMutation<
    DeactivateCallPlanMutation,
    DeactivateCallPlanMutationVariables
  >(DEACTIVATE_CALL_PLAN_MUTATION, { refetchQueries: ["GetSingleUser"] });

  const [reactivateCallPlan, { loading: reactivateInProgress }] = useMutation<
    ReactivateCallPlanMutation,
    ReactivateCallPlanMutationVariables
  >(REACTIVATE_CALL_PLAN_MUTATION, { refetchQueries: ["GetSingleUser"] });

  const [removeInteractions, { loading: removeInteractionsSaving }] = useMutation<
    RemoveInteractionsMutation,
    RemoveInteractionsMutationVariables
  >(REMOVE_INTERACTIONS_MUTATION, {
    refetchQueries: ["GetSingleUser"],
  });

  const detailLoading = useMemo(
    () => Boolean(callPlanToView && !detailedCallPlan && !error && (loading || data === undefined)),
    [callPlanToView, detailedCallPlan, error, loading, data]
  );
  const detailMissing = useMemo(
    () =>
      Boolean(
        callPlanToView &&
          !detailedCallPlan &&
          !loading &&
          !error &&
          data !== undefined &&
          !data.callPlan
      ),
    [callPlanToView, detailedCallPlan, loading, error, data]
  );
  const sortedInteractions = useMemo(() => {
    const interactions = detailedCallPlan?.interactions;
    if (!interactions?.length) {
      return [];
    }

    return [...interactions].sort((a, b) => a.order - b.order);
  }, [detailedCallPlan?.interactions]);
  const pendingRemovalIdLookup = useMemo(
    () => Object.fromEntries(pendingRemovalInteractionIds.map((id) => [id, true])),
    [pendingRemovalInteractionIds]
  );
  const visiblyListedInteractions = useMemo(
    () => sortedInteractions.filter((row) => !pendingRemovalIdLookup[row.id]),
    [pendingRemovalIdLookup, sortedInteractions]
  );
  const estimatedLengthSeconds = useMemo(
    () =>
      estimateCallPlanDetailSeconds(
        sortedInteractions.filter((row) => !pendingRemovalIdLookup[row.id])
      ),
    [pendingRemovalIdLookup, sortedInteractions]
  );
  const interactionRemoveBlockedReason = useMemo(
    () => interactionRemoveBlockedReasonForEndedPlanOnly(detailedCallPlan),
    [detailedCallPlan]
  );
  const hasInteractableStaging = useMemo(
    () => Boolean(detailedCallPlan && !interactionRemoveBlockedReason),
    [detailedCallPlan, interactionRemoveBlockedReason]
  );
  const showSaveChanges = useMemo(
    () =>
      Boolean(
        detailedCallPlan && hasInteractableStaging && pendingRemovalInteractionIds.length > 0
      ),
    [detailedCallPlan, hasInteractableStaging, pendingRemovalInteractionIds.length]
  );
  const unsavedStagingDialogContent = useMemo(
    () => (
      <>
        <Typography variant="subtitle1" component="span" sx={{ mb: 1, display: "block" }}>
          Unsaved removals
        </Typography>
        <Typography variant="body2">
          You have marked one or more steps for removal from this call plan. Save changes?
        </Typography>
      </>
    ),
    []
  );
  const deleteConfirmDialogContent = useMemo(
    () => <Typography variant="body2">Delete this call plan? This cannot be undone.</Typography>,
    []
  );
  const deleteErrorSnackbarTransitionProps = useMemo(
    () => ({
      onExited: () => {
        setDeleteErrorMessage(null);
      },
    }),
    []
  );
  const deactivateErrorSnackbarTransitionProps = useMemo(
    () => ({
      onExited: () => {
        setDeactivateErrorMessage(null);
      },
    }),
    []
  );
  const removeInteractionsErrorSnackbarTransitionProps = useMemo(
    () => ({
      onExited: () => {
        setRemoveInteractionsErrorMessage(null);
      },
    }),
    []
  );
  const reactivateErrorSnackbarTransitionProps = useMemo(
    () => ({
      onExited: () => {
        setReactivateErrorMessage(null);
      },
    }),
    []
  );
  const metaEstimatedLengthSeconds = useMemo(
    () => (detailedCallPlan ? estimatedLengthSeconds : 0),
    [detailedCallPlan, estimatedLengthSeconds]
  );

  const reportRemoveInteractionsSaveError = useCallback((message: string) => {
    setRemoveInteractionsSuccessOpen(false);
    setRemoveInteractionsErrorMessage(message);
    setRemoveInteractionsErrorOpen(true);
  }, []);

  const reportDeleteError = useCallback((message: string) => {
    setDeleteErrorMessage(message);
    setDeleteErrorOpen(true);
  }, []);

  const reportDeactivateError = useCallback((message: string) => {
    setDeactivateErrorMessage(message);
    setDeactivateErrorOpen(true);
  }, []);

  const finalizeCloseModal = useCallback(() => {
    setDeleteConfirmOpen(false);
    setDeactivateConfirmOpen(false);
    setDeleteErrorOpen(false);
    setDeleteErrorMessage(null);
    setDeactivateErrorOpen(false);
    setDeactivateErrorMessage(null);
    setRemoveInteractionsErrorOpen(false);
    setRemoveInteractionsErrorMessage(null);
    setRemoveInteractionsSuccessOpen(false);
    setUnsavedStagingClosePromptOpen(false);
    setPendingRemovalInteractionIds([]);
    setDetailedCallPlan(null);
    onClose();
  }, [onClose]);

  const requestCloseModal = useCallback(() => {
    if (pendingRemovalInteractionIds.length > 0) {
      setUnsavedStagingClosePromptOpen(true);
      return;
    }
    finalizeCloseModal();
  }, [finalizeCloseModal, pendingRemovalInteractionIds.length]);

  const persistStagedRemovalIds = useCallback(async () => {
    if (!pendingRemovalInteractionIds.length || !callPlanToView || !detailedCallPlan) {
      return false;
    }
    const ids = [...pendingRemovalInteractionIds];

    try {
      const result = await removeInteractions({
        variables: { input: { callPlanId: detailedCallPlan.id, interactionIds: ids } },
      });
      if (!result.data?.removeInteractions) {
        reportRemoveInteractionsSaveError(
          formatGqlExecutionErrors(result) ?? "Could not update call plan interactions."
        );
        return false;
      }

      const next = result.data.removeInteractions;
      setDetailedCallPlan(next);

      setPendingRemovalInteractionIds([]);
      setUnsavedStagingClosePromptOpen(false);
      setRemoveInteractionsSuccessOpen(true);
      return true;
    } catch (e) {
      reportRemoveInteractionsSaveError(
        graphqlClientErrorMessage(e, "Could not update call plan interactions.")
      );
      return false;
    }
  }, [
    callPlanToView,
    detailedCallPlan,
    pendingRemovalInteractionIds,
    removeInteractions,
    reportRemoveInteractionsSaveError,
  ]);

  const handleStageRemoveInteraction = useCallback((interactionId: number) => {
    setPendingRemovalInteractionIds((prev) =>
      prev.includes(interactionId) ? prev : [...prev, interactionId]
    );
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!callPlanToView) {
      return;
    }
    setDeleteConfirmOpen(false);
    try {
      const result = await deleteCallPlan({ variables: { id: callPlanToView.id } });
      if (result.data?.deleteCallPlan !== true) {
        reportDeleteError(formatGqlExecutionErrors(result) ?? "Could not delete the call plan.");
        return;
      }
      finalizeCloseModal();
    } catch (e) {
      reportDeleteError(graphqlClientErrorMessage(e, "Could not delete the call plan."));
    }
  }, [callPlanToView, deleteCallPlan, finalizeCloseModal, reportDeleteError]);

  const handleConfirmDeactivate = useCallback(async () => {
    if (!callPlanToView?.isActive) {
      return;
    }
    setDeactivateConfirmOpen(false);
    try {
      const result = await deactivateCallPlan({ variables: { id: callPlanToView.id } });
      if (result.data?.deactivateCallPlan !== true) {
        reportDeactivateError(
          formatGqlExecutionErrors(result) ?? "Could not deactivate the call plan."
        );
        return;
      }
      finalizeCloseModal();
    } catch (e) {
      reportDeactivateError(graphqlClientErrorMessage(e, "Could not deactivate the call plan."));
    }
  }, [callPlanToView, deactivateCallPlan, finalizeCloseModal, reportDeactivateError]);

  const reportReactivateError = useCallback((message: string) => {
    setReactivateErrorMessage(message);
    setReactivateErrorOpen(true);
  }, []);

  const handleConfirmReactivate = useCallback(async () => {
    if (!callPlanToView || !canReactivateCallPlan(callPlanToView, callPlans, accountTimeZone)) {
      return;
    }
    setReactivateConfirmOpen(false);
    try {
      const result = await reactivateCallPlan({ variables: { id: callPlanToView.id } });
      if (result.data?.reactivateCallPlan !== true) {
        reportReactivateError(
          formatGqlExecutionErrors(result) ?? "Could not reactivate the call plan."
        );
        return;
      }
      finalizeCloseModal();
    } catch (e) {
      reportReactivateError(graphqlClientErrorMessage(e, "Could not reactivate the call plan."));
    }
  }, [
    accountTimeZone,
    callPlanToView,
    callPlans,
    finalizeCloseModal,
    reactivateCallPlan,
    reportReactivateError,
  ]);

  const handleHeaderRequestSaveChanges = useCallback(() => {
    persistStagedRemovalIds();
  }, [persistStagedRemovalIds]);

  const handleHeaderRequestDelete = useCallback(() => {
    setDeleteConfirmOpen(true);
  }, []);

  const handleHeaderRequestDeactivate = useCallback(() => {
    setDeactivateConfirmOpen(true);
  }, []);

  const handleHeaderRequestReactivate = useCallback(() => {
    setReactivateConfirmOpen(true);
  }, []);

  const handleDeactivateConfirmClose = useCallback(() => {
    setDeactivateConfirmOpen(false);
  }, []);

  const handleDeactivateErrorClose = useCallback(() => {
    setDeactivateErrorOpen(false);
  }, []);

  const handleReactivateConfirmClose = useCallback(() => {
    setReactivateConfirmOpen(false);
  }, []);

  const handleReactivateErrorClose = useCallback(() => {
    setReactivateErrorOpen(false);
  }, []);

  const handleUnsavedStagingClose = useCallback(() => {
    setUnsavedStagingClosePromptOpen(false);
    finalizeCloseModal();
  }, [finalizeCloseModal]);

  const handleUnsavedStagingSubmit = useCallback(() => {
    persistStagedRemovalIds();
    finalizeCloseModal();
  }, [finalizeCloseModal, persistStagedRemovalIds]);

  const handleDeleteConfirmClose = useCallback(() => {
    setDeleteConfirmOpen(false);
  }, []);

  const handleDeleteErrorClose = useCallback(() => {
    setDeleteErrorOpen(false);
  }, []);

  const handleRemoveInteractionsSuccessClose = useCallback(() => {
    setRemoveInteractionsSuccessOpen(false);
  }, []);

  const handleRemoveInteractionsErrorClose = useCallback(() => {
    setRemoveInteractionsErrorOpen(false);
  }, []);
  const stagedRemoveHandler = useMemo(
    () => (hasInteractableStaging ? handleStageRemoveInteraction : undefined),
    [handleStageRemoveInteraction, hasInteractableStaging]
  );

  useEffect(() => {
    setPendingRemovalInteractionIds([]);
    setUnsavedStagingClosePromptOpen(false);
    setRemoveInteractionsSuccessOpen(false);
  }, [callPlanToView?.id]);

  useEffect(() => {
    if (!callPlanToView) {
      setDetailedCallPlan(null);
    }
  }, [callPlanToView]);

  useEffect(() => {
    if (!callPlanToView) {
      return;
    }

    const row = data?.callPlan;

    if (row && row.id === callPlanToView.id) {
      setDetailedCallPlan(row);
    }
  }, [data?.callPlan, callPlanToView]);

  const showReactivate = useMemo(
    () =>
      Boolean(callPlanToView && canReactivateCallPlan(callPlanToView, callPlans, accountTimeZone)),
    [accountTimeZone, callPlanToView, callPlans]
  );

  if (!open || !callPlanToView) {
    return null;
  }

  return (
    <>
      <Modal open={open} onClose={requestCloseModal}>
        <Box sx={modalContainerSx}>
          <ViewCallPlanModalHeader
            callPlanToView={callPlanToView}
            onRequestCloseModal={requestCloseModal}
            editState={editState}
            deleteState={deleteState}
            deleteInProgress={deleteInProgress}
            deactivateInProgress={deactivateInProgress}
            reactivateInProgress={reactivateInProgress}
            showReactivate={showReactivate}
            showSaveChanges={showSaveChanges}
            saveChangesInProgress={removeInteractionsSaving}
            onRequestSaveChanges={handleHeaderRequestSaveChanges}
            onRequestDelete={handleHeaderRequestDelete}
            onRequestDeactivate={handleHeaderRequestDeactivate}
            onRequestReactivate={handleHeaderRequestReactivate}
            onRequestEdit={onRequestEdit}
          />

          <Box sx={sx.viewCallPlanModalBodySx}>
            <PatientTimezoneDateBlurb accountTimeZone={accountTimeZone} sx={{ mb: 1 }} />
            <ViewCallPlanMetaSection
              accountTimeZone={accountTimeZone}
              callPlan={callPlanToView}
              estimatedLengthSeconds={metaEstimatedLengthSeconds}
            />

            <Box sx={sx.viewCallPlanScrollViewportSx}>
              <Box sx={sx.viewCallPlanScrollContentSx}>
                {detailLoading && (
                  <Box sx={sx.viewCallPlanScrollSpinnerWrapSx}>
                    <CircularProgress size={40} />
                  </Box>
                )}
                {!detailedCallPlan && !loading && error && (
                  <Typography color="error" variant="body2">
                    Could not load this call plan.
                  </Typography>
                )}
                {detailMissing && (
                  <Typography variant="body2" color="text.secondary">
                    Call plan not found.
                  </Typography>
                )}
                {detailedCallPlan && (
                  <ViewCallPlanBody
                    loading={false}
                    error={undefined}
                    sortedInteractions={visiblyListedInteractions}
                    interactionRemoveBlockedReason={interactionRemoveBlockedReason}
                    onStageRemoveInteraction={stagedRemoveHandler}
                    interactionStagingFrozen={removeInteractionsSaving}
                  />
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Modal>

      {unsavedStagingClosePromptOpen && (
        <ConfirmDialog
          confirmOpen={unsavedStagingClosePromptOpen}
          dialogContent={unsavedStagingDialogContent}
          onClose={handleUnsavedStagingClose}
          onSubmit={handleUnsavedStagingSubmit}
        />
      )}

      {deleteConfirmOpen && (
        <ConfirmDialog
          confirmOpen={deleteConfirmOpen}
          dialogContent={deleteConfirmDialogContent}
          onClose={handleDeleteConfirmClose}
          onSubmit={handleConfirmDelete}
        />
      )}

      {deactivateConfirmOpen && (
        <ConfirmDialog
          confirmOpen={deactivateConfirmOpen}
          dialogContent={deactivateCallPlanConfirmDialogContent}
          onClose={handleDeactivateConfirmClose}
          onSubmit={handleConfirmDeactivate}
        />
      )}

      {reactivateConfirmOpen && (
        <ConfirmDialog
          confirmOpen={reactivateConfirmOpen}
          dialogContent={reactivateCallPlanConfirmDialogContent}
          onClose={handleReactivateConfirmClose}
          onSubmit={handleConfirmReactivate}
        />
      )}

      {deleteErrorOpen && (
        <Snackbar
          open={deleteErrorOpen}
          autoHideDuration={CALL_PLAN_SNACKBAR_AUTO_HIDE_MS}
          anchorOrigin={sx.viewCallPlanSnackbarAnchorTopRight}
          onClose={callPlanSnackbarOnClose(handleDeleteErrorClose)}
          TransitionProps={deleteErrorSnackbarTransitionProps}
        >
          <Alert
            onClose={handleDeleteErrorClose}
            severity="error"
            variant="filled"
            sx={sx.viewCallPlanAlertFullWidthSx}
          >
            {deleteErrorMessage ?? "Could not delete the call plan."}
          </Alert>
        </Snackbar>
      )}

      {deactivateErrorOpen && (
        <Snackbar
          open={deactivateErrorOpen}
          autoHideDuration={CALL_PLAN_SNACKBAR_AUTO_HIDE_MS}
          anchorOrigin={sx.viewCallPlanSnackbarAnchorTopRight}
          onClose={callPlanSnackbarOnClose(handleDeactivateErrorClose)}
          TransitionProps={deactivateErrorSnackbarTransitionProps}
        >
          <Alert
            onClose={handleDeactivateErrorClose}
            severity="error"
            variant="filled"
            sx={sx.viewCallPlanAlertFullWidthSx}
          >
            {deactivateErrorMessage ?? "Could not deactivate the call plan."}
          </Alert>
        </Snackbar>
      )}

      {reactivateErrorOpen && (
        <Snackbar
          open={reactivateErrorOpen}
          autoHideDuration={CALL_PLAN_SNACKBAR_AUTO_HIDE_MS}
          anchorOrigin={sx.viewCallPlanSnackbarAnchorTopRight}
          onClose={callPlanSnackbarOnClose(handleReactivateErrorClose)}
          TransitionProps={reactivateErrorSnackbarTransitionProps}
        >
          <Alert
            onClose={handleReactivateErrorClose}
            severity="error"
            variant="filled"
            sx={sx.viewCallPlanAlertFullWidthSx}
          >
            {reactivateErrorMessage ?? "Could not reactivate the call plan."}
          </Alert>
        </Snackbar>
      )}

      {removeInteractionsSuccessOpen && (
        <Snackbar
          open={removeInteractionsSuccessOpen}
          autoHideDuration={CALL_PLAN_SNACKBAR_AUTO_HIDE_MS}
          anchorOrigin={sx.viewCallPlanSnackbarAnchorTopRight}
          onClose={callPlanSnackbarOnClose(handleRemoveInteractionsSuccessClose)}
        >
          <Alert
            onClose={handleRemoveInteractionsSuccessClose}
            severity="success"
            variant="filled"
            sx={sx.viewCallPlanAlertFullWidthSx}
          >
            Changes saved.
          </Alert>
        </Snackbar>
      )}

      {removeInteractionsErrorOpen && (
        <Snackbar
          open={removeInteractionsErrorOpen}
          autoHideDuration={CALL_PLAN_SNACKBAR_AUTO_HIDE_MS}
          anchorOrigin={sx.viewCallPlanSnackbarAnchorTopRight}
          onClose={callPlanSnackbarOnClose(handleRemoveInteractionsErrorClose)}
          TransitionProps={removeInteractionsErrorSnackbarTransitionProps}
        >
          <Alert
            onClose={handleRemoveInteractionsErrorClose}
            severity="error"
            variant="filled"
            sx={sx.viewCallPlanAlertFullWidthSx}
          >
            {removeInteractionsErrorMessage ?? "Could not update call plan interactions."}
          </Alert>
        </Snackbar>
      )}
    </>
  );
};

export default ViewCallPlanModal;
